// BUG-2026-0121 #1 — Quick "New Internal Quote" entry.
// Internal quotes skip the approval workflow and can be added to the schedule
// directly, so for internal/own-lab work the user wants a fast, low-information
// start: just type a project name and go. Client, line items (test quantities),
// and any other detail are filled in afterward on the (streamlined) editor.
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { createQuote } from '../../services/quoteService';
import { ProjectTitlePicker } from './ProjectTitlePicker';

interface QuickAddInternalQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new quote's key so the caller can navigate to the editor. */
  onCreated: (quoteKey: string) => void;
}

export const QuickAddInternalQuoteDialog = ({
  open, onClose, onCreated,
}: QuickAddInternalQuoteDialogProps) => {
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setTitle(''); setError(''); setSubmitting(false); }
  }, [open]);

  const handleSubmit = async (explicitTitle?: string) => {
    const t = (explicitTitle ?? title).trim();
    if (!t) { setError('Project name is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      const q = await createQuote({ QuoteType: 'Internal', Title: t });
      onCreated(q.QuoteKey);
    } catch {
      setError('Failed to create the internal quote.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New Internal Quote</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <Alert severity="info" sx={{ py: 0.5 }}>
          Internal quotes skip approval and can be added straight to the schedule.
          Add the client and test quantities next on the editor.
        </Alert>
        {/* BUG-2026-0202: pick an existing project (or add one) instead of free-typing. */}
        <ProjectTitlePicker
          label="Project"
          required
          autoFocus
          value={title}
          onInput={setTitle}
          onCommit={(t) => setTitle(t ?? '')}
          onEnter={(t) => { if (!submitting) void handleSubmit(t); }}
        />
        <Typography variant="caption" color="text.secondary">
          You can rename it, pick a client, and add line items afterward.
        </Typography>
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Create &amp; Open
        </Button>
      </DialogActions>
    </Dialog>
  );
};
